import { formatDisplayDate } from './dateDisplay';

/**
 * The words for an account's email delivery state — why keepr has stopped
 * mailing the address — shared by the bounce banner, the Profile note, the
 * Email master row and the session-less preferences page, so the four never
 * describe one stop four ways.
 *
 * `emailDelivery` (on the current user, from `GET /api/user-info`):
 * `{ state: 'bounced' | 'complained' | 'suppressed', reason, address, at,
 * until, suppressionId, resumeLockedUntil }`, absent while mail flows.
 *
 *   bounced     a hard bounce, or three soft bounces in a week (then `until`
 *               is when the 72-hour pause ends — it lifts itself)
 *   complained  THIS person reported a keepr mail as spam: their Email master
 *               went off with it, and only they can resume (plan §3.5)
 *   suppressed  an administrator's stop, or another account's spam report at
 *               the same inbox (`reason` says which)
 */

/** The delivery state, or null while mail flows. */
export function deliveryOf(user) {
	const d = user && user.emailDelivery;
	return d && typeof d === 'object' && d.state ? d : null;
}

const dateOf = value => (value ? formatDisplayDate(value) : null);

/**
 * "A keepr email to ada@… was reported as spam on 12 Sep 2026" — no full stop.
 * `{ lower: true }` for the middle of a sentence ("Off because a keepr email…").
 */
export function complaintSentence(delivery, fallbackAddress = '', { lower = false } = {}) {
	const address = (delivery && delivery.address) || fallbackAddress;
	const at = delivery && dateOf(delivery.at);
	return `${lower ? 'a' : 'A'} keepr email to ${address} was reported as spam${at ? ` on ${at}` : ''}`;
}

/** The date self-service resume unlocks, when a second report locked it; else null. */
export function resumeLockedUntil(delivery, now = new Date()) {
	const raw = delivery && delivery.resumeLockedUntil;
	if (!raw) return null;
	const at = new Date(raw);
	return Number.isNaN(at.getTime()) || at <= now ? null : at;
}

/**
 * One sentence for a stop that is NOT a spam report by this person — what the
 * banner says, and the Profile note's first line.
 */
export function stopSentence(delivery) {
	if (!delivery) return '';
	const address = delivery.address || 'your address';
	if (delivery.state === 'bounced' && delivery.until) {
		return `Email to ${address} keeps failing, so keepr has paused sending to it until ${dateOf(delivery.until)} — sign-in and notification emails included. It starts again by itself.`;
	}
	if (delivery.state === 'bounced') {
		return `We couldn't deliver to ${address}, so keepr has stopped sending to it — sign-in and notification emails included.`;
	}
	if (delivery.state === 'suppressed') {
		return delivery.reason === 'complaint'
			? `Someone using ${address} reported a keepr email as spam, so keepr has stopped sending to it — sign-in and notification emails included.`
			: `A keepr administrator has stopped email to ${address} — sign-in and notification emails included.`;
	}
	return '';
}
